import React, { useEffect, useState } from 'react';
import { collection, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Agencia {
  id: string;
  nombre: string;
  correo?: string;
  telefono?: string;
  creado_auto?: boolean;
  verificacion_pendiente?: boolean;
  mls_office_key?: string;
  activo?: boolean;
  propsMls?: number;
  [key: string]: any;
}

export default function AdminAgencias() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('verificadas');
  const [paginaActual, setPaginaActual] = useState(1);
  const [loading, setLoading] = useState(true);
  const agenciasPorPagina = 15;

  useEffect(() => {
    const fetchAgencias = async () => {
      try {
        // Cargar agencias
        const snap = await getDocs(collection(db, 'inmobiliaria'));
        const data: Agencia[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as Agencia));

        // Pre-cargar conteo de propiedades MLS por agencia
        const propsMlsSnap = await getDocs(
          query(collection(db, 'propiedades'), where('fuente', '==', 'flexmls'), where('estado', '==', 'A'))
        );
        const propsPorAgencia = new Map<string, number>();
        propsMlsSnap.docs.forEach(d => {
          const inmId = d.data().inmobiliaria_doc_id;
          if (inmId) propsPorAgencia.set(inmId, (propsPorAgencia.get(inmId) || 0) + 1);
        });

        // Asignar conteo a cada agencia
        data.forEach(a => {
          a.propsMls = propsPorAgencia.get(a.id) || 0;
        });

        data.sort((a, b) => (a.nombre || '').toLowerCase().localeCompare((b.nombre || '').toLowerCase()));
        setAgencias(data);
      } catch (error) {
        console.error('Error cargando agencias:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAgencias();
  }, []);

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <h2>No tienes permiso para acceder a esta seccion</h2>
      </div>
    );
  }

  const handleDelete = async (agencia: Agencia) => {
    // Bloquear delete en agencias MLS
    if (agencia.mls_office_key || agencia.creado_auto) {
      Swal.fire({
        icon: 'info',
        title: 'Agencia MLS',
        text: 'Esta agencia fue creada por el sync MLS. No se puede eliminar porque se recrearia en la proxima sincronizacion.',
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Eliminar agencia?',
      text: 'Se eliminaran sus afiliados, asociados, propiedades y proyectos',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Si, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    try {
      const docId = agencia.id;

      const afiliadosSnap = await getDocs(query(collection(db, 'asesor'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of afiliadosSnap.docs) await deleteDoc(doc(db, 'asesor', docu.id));

      const asociadosSnap = await getDocs(query(collection(db, 'asociado'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of asociadosSnap.docs) await deleteDoc(doc(db, 'asociado', docu.id));

      const propSnap = await getDocs(query(collection(db, 'propiedades'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of propSnap.docs) await deleteDoc(doc(db, 'propiedades', docu.id));

      const projSnap = await getDocs(query(collection(db, 'proyectos'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of projSnap.docs) await deleteDoc(doc(db, 'proyectos', docu.id));

      await deleteDoc(doc(db, 'inmobiliaria', docId));
      setAgencias(prev => prev.filter(a => a.id !== docId));

      Swal.fire({ icon: 'success', title: 'Agencia eliminada', text: 'La agencia y sus elementos han sido eliminados.' });
    } catch (error) {
      console.error('Error al eliminar agencia:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Hubo un problema al eliminar la agencia.' });
    }
  };

  // Filtros
  const agenciasFiltradas = agencias.filter(a => {
    const matchTexto = (a.nombre || '').toLowerCase().includes(filtro.toLowerCase()) ||
                       (a.correo || '').toLowerCase().includes(filtro.toLowerCase());
    let matchTipo = true;
    if (filtroTipo === 'verificadas') {
      matchTipo = !a.verificacion_pendiente;
    } else if (filtroTipo === 'pendiente') {
      matchTipo = !!a.verificacion_pendiente;
    } else if (filtroTipo === 'mls') {
      matchTipo = !!(a.mls_office_key || a.creado_auto);
    } else if (filtroTipo === 'manual') {
      matchTipo = !a.mls_office_key && !a.creado_auto;
    }
    // filtroTipo === '' -> todas
    return matchTexto && matchTipo;
  });

  // Paginacion
  const totalPaginas = Math.ceil(agenciasFiltradas.length / agenciasPorPagina);
  const startIndex = (paginaActual - 1) * agenciasPorPagina;
  const agenciasPaginadas = agenciasFiltradas.slice(startIndex, startIndex + agenciasPorPagina);

  // Stats
  const totalVerificadas = agencias.filter(a => !a.verificacion_pendiente).length;
  const totalPendientes = agencias.filter(a => a.verificacion_pendiente).length;
  const totalMls = agencias.filter(a => a.mls_office_key || a.creado_auto).length;

  return (
    <>
      <UserNav />
      <div className="container-fluid px-4 mt-4">
        <Breadcrumb items={[
          { label: 'Dashboard', path: '/admin-dashboard' },
          { label: 'Agencias' }
        ]} />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">
            <i className="fas fa-building me-2 text-primary"></i>Agencias
          </h2>
        </div>

        {/* Stats compactos */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <span
            className={`badge fs-6 p-2 ${filtroTipo === 'verificadas' ? 'bg-success' : 'bg-outline-success border border-success text-success'}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setFiltroTipo('verificadas')}
          >
            <i className="fas fa-check-circle me-1"></i>{totalVerificadas} Verificadas
          </span>
          {totalPendientes > 0 && (
            <span
              className={`badge fs-6 p-2 ${filtroTipo === 'pendiente' ? 'bg-warning text-dark' : 'bg-outline-warning border border-warning text-warning'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setFiltroTipo(filtroTipo === 'pendiente' ? 'verificadas' : 'pendiente')}
            >
              <i className="fas fa-clock me-1"></i>{totalPendientes} Pendientes verificar
            </span>
          )}
          <span
            className={`badge fs-6 p-2 ${filtroTipo === '' ? 'bg-primary' : 'bg-outline-primary border border-primary text-primary'}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setFiltroTipo('')}
          >
            {agencias.length} Todas
          </span>
          <span className="badge bg-secondary fs-6 p-2">
            <i className="fas fa-filter me-1"></i>{agenciasFiltradas.length} mostradas
          </span>
        </div>

        {/* Filtros */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2">
              <div className="col-md-5">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar por nombre o correo..."
                  value={filtro}
                  onChange={(e) => { setFiltro(e.target.value); setPaginaActual(1); }}
                />
              </div>
              <div className="col-md-3">
                <select
                  className="form-select form-select-sm"
                  value={filtroTipo}
                  onChange={(e) => { setFiltroTipo(e.target.value); setPaginaActual(1); }}
                >
                  <option value="verificadas">Verificadas</option>
                  <option value="pendiente">Pendientes verificar</option>
                  <option value="">Todas</option>
                  <option value="mls">Solo MLS</option>
                  <option value="manual">Solo manuales</option>
                </select>
              </div>
              <div className="col-md-1">
                <button
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={() => { setFiltro(''); setFiltroTipo(''); }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Cargando agencias...</p>
          </div>
        ) : agenciasFiltradas.length === 0 ? (
          <div className="alert alert-info text-center">No hay agencias que coincidan con los filtros.</div>
        ) : (
          <>
            <div className="table-responsive">
              <table aria-label="Tabla de Admin Inm" className="table table-hover table-sm align-middle">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '30%' }}>Agencia</th>
                    <th scope="col">Correo</th>
                    <th scope="col">Telefono</th>
                    <th scope="col">Props MLS</th>
                    <th style={{ width: '100px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {agenciasPaginadas.map(agencia => {
                    const esMls = !!(agencia.mls_office_key || agencia.creado_auto);
                    return (
                      <tr key={agencia.id}>
                        <td>
                          <div className="fw-semibold">{agencia.nombre}</div>
                          <div className="d-flex gap-1 mt-1 flex-wrap">
                            {esMls && (
                              <span className="badge bg-info" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-database me-1"></i>MLS
                              </span>
                            )}
                            {agencia.verificacion_pendiente && (
                              <span className="badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-clock me-1"></i>Pendiente
                              </span>
                            )}
                            {agencia.activo === false && (
                              <span className="badge bg-danger" style={{ fontSize: '0.65rem' }}>Inactiva</span>
                            )}
                          </div>
                        </td>
                        <td><small>{agencia.correo || <span className="text-muted">-</span>}</small></td>
                        <td><small>{agencia.telefono || <span className="text-muted">-</span>}</small></td>
                        <td>
                          <span className={`badge ${agencia.propsMls && agencia.propsMls > 0 ? 'bg-success' : 'bg-secondary'}`}>
                            {agencia.propsMls || 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-primary"
                              onClick={() => navigate(`/ver-inm/${agencia.id}`)}
                              title="Ver detalle"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="btn btn-outline-warning"
                              onClick={() => navigate(`/edit-inmobiliaria/${agencia.id}`)}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            {!esMls && (
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDelete(agencia)}
                                title="Eliminar"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginacion */}
            <div className="d-flex justify-content-between align-items-center mt-3 mb-4">
              <small className="text-muted">
                {startIndex + 1}-{Math.min(startIndex + agenciasPorPagina, agenciasFiltradas.length)} de {agenciasFiltradas.length}
              </small>
              {totalPaginas > 1 && (
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual - 1)} disabled={paginaActual === 1}>
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: Math.min(7, totalPaginas) }, (_, i) => {
                      let pageNum: number;
                      if (totalPaginas <= 7) pageNum = i + 1;
                      else if (paginaActual <= 4) pageNum = i + 1;
                      else if (paginaActual >= totalPaginas - 3) pageNum = totalPaginas - 6 + i;
                      else pageNum = paginaActual - 3 + i;
                      return (
                        <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(pageNum)}>{pageNum}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual + 1)} disabled={paginaActual === totalPaginas}>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </>
        )}
      </div>
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
